/**
 * Workspace-configurable predictability profiles (Slice 4 finish).
 * Stored on planning_workspace_defaults.predictability_profiles.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  BUILTIN_PREDICTABILITY_PROFILES,
  type PredictabilityProfile,
} from './calendarPredictability';

export type ProfileSlot = 'experimental' | 'solid' | 'default';

export interface PredictabilityProfileOverride {
  uncertainty_factor: number;
  confidence_ceiling: number;
  commitment_firmness: number;
}

export type WorkspacePredictabilityProfiles = Partial<
  Record<ProfileSlot, PredictabilityProfileOverride>
>;

const SLOTS: ProfileSlot[] = ['experimental', 'solid', 'default'];

export function defaultPredictabilityOverrides(): Record<ProfileSlot, PredictabilityProfileOverride> {
  const out = {} as Record<ProfileSlot, PredictabilityProfileOverride>;
  for (const slot of SLOTS) {
    const b = BUILTIN_PREDICTABILITY_PROFILES[slot];
    out[slot] = {
      uncertainty_factor: b.uncertainty_factor,
      confidence_ceiling: b.confidence_ceiling,
      commitment_firmness: b.commitment_firmness,
    };
  }
  return out;
}

export async function getWorkspacePredictabilityProfiles(
  workspaceId: string,
): Promise<WorkspacePredictabilityProfiles> {
  const { data, error } = await getSupabaseClient()
    .from('planning_workspace_defaults')
    .select('predictability_profiles')
    .eq('workspace_id', workspaceId)
    .maybeSingle();
  if (error) {
    console.warn('[getWorkspacePredictabilityProfiles]', error.message);
    return {};
  }
  const raw = (data?.predictability_profiles ?? {}) as WorkspacePredictabilityProfiles;
  return raw && typeof raw === 'object' ? raw : {};
}

export async function saveWorkspacePredictabilityProfiles(
  workspaceId: string,
  profiles: WorkspacePredictabilityProfiles,
): Promise<void> {
  const sb = getSupabaseClient();
  const { data: existing } = await sb
    .from('planning_workspace_defaults')
    .select('workspace_id')
    .eq('workspace_id', workspaceId)
    .maybeSingle();

  if (existing) {
    const { error } = await sb
      .from('planning_workspace_defaults')
      .update({
        predictability_profiles: profiles,
        updated_at: new Date().toISOString(),
      })
      .eq('workspace_id', workspaceId);
    if (error) throw error;
    return;
  }

  const { error } = await sb.from('planning_workspace_defaults').insert({
    workspace_id: workspaceId,
    predictability_profiles: profiles,
  });
  if (error) throw error;
}

/** Merge builtins + workspace overrides into the shape derivePredictability expects. */
export function toDerivePredictabilityWorkspaceProfiles(
  overrides: WorkspacePredictabilityProfiles,
): Parameters<typeof import('./calendarPredictability').derivePredictability>[0]['workspaceProfiles'] {
  const base = BUILTIN_PREDICTABILITY_PROFILES;
  const out: NonNullable<
    Parameters<typeof import('./calendarPredictability').derivePredictability>[0]['workspaceProfiles']
  > = {};
  for (const slot of SLOTS) {
    const o = overrides[slot];
    if (!o) continue;
    out[slot] = {
      ...base[slot],
      uncertainty_factor: o.uncertainty_factor,
      confidence_ceiling: o.confidence_ceiling,
      commitment_firmness: o.commitment_firmness,
    };
  }
  return out;
}

export function profilePreview(slot: ProfileSlot, o: PredictabilityProfileOverride): Pick<
  PredictabilityProfile,
  'slackWeeks' | 'calendarConfidence' | 'suggestedFlexibility'
> {
  const slackWeeks = Math.max(0, Math.round((o.uncertainty_factor - 1) * 2));
  return {
    slackWeeks,
    calendarConfidence: o.confidence_ceiling,
    suggestedFlexibility: slot === 'solid' ? 'fixed' : 'flexible',
  };
}
